import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { useForbiddenGuard } from '../api/guard'
import './screens.css'

interface FeatureFlag {
  flag_key: string
  enabled: boolean
  rollout_percentage: number
  description: string | null
  updated_at: string | null
}
interface FlagsResponse {
  flags: FeatureFlag[]
}

type PendingToggle = { flag: FeatureFlag; nextEnabled: boolean }

export function FeatureFlagsView() {
  const qc = useQueryClient()
  const [pending, setPending] = useState<PendingToggle | null>(null)

  const query = useQuery<FlagsResponse>({
    queryKey: ['admin-feature-flags'],
    queryFn: () => apiFetch<FlagsResponse>('/admin/feature-flags'),
  })
  useForbiddenGuard(query.error)

  const mutation = useMutation({
    mutationFn: (vars: { flag_key: string; enabled: boolean }) =>
      apiFetch(`/admin/feature-flags/${encodeURIComponent(vars.flag_key)}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: vars.enabled }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-feature-flags'] }),
  })
  useForbiddenGuard(mutation.error)

  const flags = query.data?.flags ?? []

  return (
    <section className="ff" aria-labelledby="ff-heading">
      <div className="screen-head">
        <h2 id="ff-heading">Feature Flags</h2>
      </div>

      {mutation.isError && (mutation.error as { status?: number }).status !== 403 && (
        <p className="screen-error" role="alert">
          Update failed.
        </p>
      )}

      <table className="ff-table">
        <thead>
          <tr>
            <th>Flag</th>
            <th>Description</th>
            <th>Rollout %</th>
            <th>Enabled</th>
          </tr>
        </thead>
        <tbody>
          {flags.map((f) => (
            <tr key={f.flag_key}>
              <td>{f.flag_key}</td>
              <td>{f.description ?? '—'}</td>
              <td>{f.rollout_percentage}</td>
              <td>
                <button
                  type="button"
                  className="screen-btn"
                  aria-label={`Toggle ${f.flag_key}`}
                  onClick={() => setPending({ flag: f, nextEnabled: !f.enabled })}
                >
                  {f.enabled ? 'On' : 'Off'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {pending && (
        <div className="screen-confirm-backdrop" role="dialog" aria-modal="true" aria-label="Confirm flag change">
          <div className="screen-confirm">
            <p>
              {pending.nextEnabled ? 'Enable' : 'Disable'} <strong>{pending.flag.flag_key}</strong> globally?
            </p>
            <div className="screen-confirm-actions">
              <button type="button" className="screen-btn" onClick={() => setPending(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="screen-btn screen-btn-primary"
                onClick={() => {
                  mutation.mutate({ flag_key: pending.flag.flag_key, enabled: pending.nextEnabled })
                  setPending(null)
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
